'use client'

import { forwardRef, useState, useCallback, useMemo } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '../utils/cn'
import { safeAccess, safeArrayAccess } from '../utils/security'

/**
 * BIRHAUS Quick Filter Component
 * 
 * Toggle-able filter chip implementing BIRHAUS principles:
 * - Spanish-first labels with English fallbacks
 * - Clear active/inactive visual states
 * - Accessibility compliance (WCAG AA+)
 * - Count badges for cognitive load transparency
 * - Consistent with BIRHAUS color scheme
 * - Performance optimized with minimal re-renders
 */

export interface BirhausQuickFilterProps {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // Visual elements
  icon?: string | LucideIcon
  count?: number
  
  // State
  active?: boolean
  disabled?: boolean
  loading?: boolean
  
  // Interactions
  onClick?: () => void | Promise<void>
  
  // BIRHAUS Principle #6: Accessibility = dignity
  ariaLabel?: string
  ariaLabelEs?: string
  ariaLabelEn?: string
  
  // Locale
  locale?: 'es' | 'en'
  
  // Styling
  variant?: 'default' | 'primary' | 'secondary'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export const BirhausQuickFilter = forwardRef<HTMLButtonElement, BirhausQuickFilterProps>(({
  // Content
  label,
  labelEs,
  labelEn,
  
  // Visual elements
  icon,
  count,
  
  // State
  active = false,
  disabled = false,
  loading = false,
  
  // Interactions
  onClick,
  
  // Accessibility
  ariaLabel,
  ariaLabelEs,
  ariaLabelEn,
  
  // Locale
  locale = 'es',
  
  // Styling
  variant = 'default',
  size = 'md',
  className,
  
  ...props
}, ref) => {
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = (
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }

  const displayLabel = getDisplayText(
    label,
    labelEs,
    labelEn,
    'Filtro'
  )

  const displayAriaLabel = getDisplayText(
    ariaLabel,
    ariaLabelEs || labelEs,
    ariaLabelEn || labelEn,
    displayLabel
  )

  // Handle icon rendering
  const Icon = typeof icon === 'function' ? icon : null
  const isEmojiIcon = typeof icon === 'string'

  // Handle async onClick
  const handleClick = async () => {
    if (disabled || loading) return
    await onClick?.()
  }

  // Size classes
  const sizeClasses = {
    sm: {
      button: 'px-2 py-1 text-xs',
      icon: 'w-3 h-3 mr-1',
      badge: 'ml-1 px-1 py-0.5 text-xs'
    },
    md: {
      button: 'px-3 py-1.5 text-sm',
      icon: 'w-4 h-4 mr-1.5',
      badge: 'ml-2 px-1.5 py-0.5 text-xs'
    },
    lg: {
      button: 'px-4 py-2 text-base',
      icon: 'w-5 h-5 mr-2',
      badge: 'ml-2 px-2 py-1 text-sm'
    }
  }

  const sizeConfig = safeAccess(sizeClasses, size, sizeClasses.md)

  // Variant classes
  const variantClasses = {
    default: active ? [
      'border-blue-600 bg-blue-50 text-blue-700',
      'hover:bg-blue-100 focus:ring-blue-500'
    ] : [
      'border-gray-200 bg-white text-gray-700',
      'hover:bg-gray-50 hover:border-gray-300 focus:ring-gray-500'
    ],
    primary: active ? [
      'border-blue-600 bg-blue-600 text-white',
      'hover:bg-blue-700 focus:ring-blue-500'
    ] : [
      'border-blue-200 bg-blue-50 text-blue-700',
      'hover:bg-blue-100 hover:border-blue-300 focus:ring-blue-500'
    ],
    secondary: active ? [
      'border-gray-600 bg-gray-600 text-white',
      'hover:bg-gray-700 focus:ring-gray-500'
    ] : [
      'border-gray-300 bg-gray-100 text-gray-700',
      'hover:bg-gray-200 hover:border-gray-400 focus:ring-gray-500'
    ]
  }

  const variantConfig = safeAccess(variantClasses, variant, variantClasses.default)
  const activeVariantClasses = Array.isArray(variantConfig) 
    ? safeArrayAccess(variantConfig, 0, '')
    : variantConfig
  const inactiveVariantClasses = Array.isArray(variantConfig)
    ? safeArrayAccess(variantConfig, 1, safeArrayAccess(variantConfig, 0, ''))
    : variantConfig

  return (
    <button
      ref={ref}
      type="button"
      onClick={handleClick}
      disabled={disabled || loading}
      aria-pressed={active}
      aria-label={displayAriaLabel}
      className={cn(
        'inline-flex items-center font-medium rounded-full',
        'border transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-offset-1',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        sizeConfig.button,
        active ? activeVariantClasses : inactiveVariantClasses,
        className
      )}
      {...props}
    >
      {/* Loading state */}
      {loading && (
        <div className="animate-spin rounded-full border-2 border-current border-t-transparent w-4 h-4 mr-1.5" />
      )}

      {/* Icon or Emoji */}
      {!loading && icon && (
        <>
          {Icon ? (
            <Icon className={sizeConfig.icon} />
          ) : isEmojiIcon ? (
            <span className="mr-1.5">{icon}</span>
          ) : null}
        </>
      )}

      {/* Label */}
      <span>{displayLabel}</span>

      {/* Count Badge */}
      {count !== undefined && (
        <span className={cn(
          'rounded-full font-medium',
          sizeConfig.badge,
          active ? (
            variant === 'default' 
              ? 'bg-blue-600 text-white'
              : variant === 'primary' 
                ? 'bg-blue-800 text-white'
                : 'bg-gray-800 text-white'
          ) : (
            variant === 'default'
              ? 'bg-gray-200 text-gray-600'
              : variant === 'primary'
                ? 'bg-blue-200 text-blue-800'
                : 'bg-gray-300 text-gray-700'
          )
        )}>
          {count}
        </span>
      )}
    </button>
  )
})

BirhausQuickFilter.displayName = 'BirhausQuickFilter'

// Hook for managing quick filter state
export function useBirhausQuickFilter(initialFilters: Array<{
  key: string
  labelEs: string
  labelEn?: string
  active?: boolean
}> = []) {
  const [filters, setFilters] = useState(initialFilters)
  
  const toggleFilter = useCallback((key: string) => {
    setFilters(prev => prev.map(filter => 
      filter.key === key 
        ? { ...filter, active: !filter.active }
        : filter
    ))
  }, [])
  
  const setFilterActive = useCallback((key: string, active: boolean) => {
    setFilters(prev => prev.map(filter =>
      filter.key === key
        ? { ...filter, active }
        : filter
    ))
  }, [])
  
  const clearAllFilters = useCallback(() => {
    setFilters(prev => prev.map(filter => ({ ...filter, active: false })))
  }, [])
  
  const activeFilters = useMemo(() => 
    filters.filter(filter => filter.active)
  , [filters])
  
  return {
    filters,
    setFilters,
    toggleFilter,
    setFilterActive,
    clearAllFilters,
    activeFilters,
    hasActiveFilters: activeFilters.length > 0,
    activeCount: activeFilters.length
  }
}

// Common filter presets for BIRHAUS applications
export const birhausQuickFilterPresets = {
  // Student management filters
  studentStatus: [
    { key: 'active', labelEs: 'Activos', labelEn: 'Active', icon: '👤' },
    { key: 'inactive', labelEs: 'Inactivos', labelEn: 'Inactive', icon: '💤' },
    { key: 'graduated', labelEs: 'Graduados', labelEn: 'Graduated', icon: '🎓' },
    { key: 'pending', labelEs: 'Pendientes', labelEn: 'Pending', icon: '⏳' }
  ],
  
  // Payment filters
  paymentStatus: [
    { key: 'paid', labelEs: 'Pagado', labelEn: 'Paid', icon: '✅' },
    { key: 'pending', labelEs: 'Pendiente', labelEn: 'Pending', icon: '⏳' },
    { key: 'overdue', labelEs: 'Vencido', labelEn: 'Overdue', icon: '⚠️' },
    { key: 'cancelled', labelEs: 'Cancelado', labelEn: 'Cancelled', icon: '❌' }
  ],
  
  // Course filters
  courseStatus: [
    { key: 'active', labelEs: 'En Curso', labelEn: 'Active', icon: '📚' },
    { key: 'completed', labelEs: 'Completado', labelEn: 'Completed', icon: '✅' },
    { key: 'scheduled', labelEs: 'Programado', labelEn: 'Scheduled', icon: '📅' },
    { key: 'cancelled', labelEs: 'Cancelado', labelEn: 'Cancelled', icon: '❌' }
  ],
  
  // Time period filters
  timePeriod: [
    { key: 'today', labelEs: 'Hoy', labelEn: 'Today', icon: '📅' },
    { key: 'week', labelEs: 'Esta Semana', labelEn: 'This Week', icon: '📊' },
    { key: 'month', labelEs: 'Este Mes', labelEn: 'This Month', icon: '🗓️' },
    { key: 'year', labelEs: 'Este Año', labelEn: 'This Year', icon: '📈' }
  ]
}